import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import {
  useGetCashMovementsQuery,
  useGetTillSessionQuery,
  useGetTillsQuery,
} from "../../store/api";
import {
  formatCurrency,
  formatSignedCurrency,
  getTenderSummaryAmount,
  getCashingUpMovementTone,
  getCashingUpMovementTypeLabel,
  getMovementSignedAmount,
  isVisibleCashMovementStatus,
  normalizeMoney,
} from "../../utils/cashingUp";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const formatShortDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const buildBackPath = ({ dateFrom, dateTo, businessDate, tillIdFilter, statusFilter }) => {
  const params = new URLSearchParams();
  const resolvedDateFrom = dateFrom || businessDate || "";
  const resolvedDateTo = dateTo || businessDate || "";
  if (resolvedDateFrom) params.set("fromDate", resolvedDateFrom);
  if (resolvedDateTo) params.set("toDate", resolvedDateTo);
  if (tillIdFilter) params.set("tillId", tillIdFilter);
  if (statusFilter) params.set("statuses", statusFilter);
  const queryString = params.toString();
  return queryString ? `/pos/cash-movements/history?${queryString}` : "/pos/cash-movements/history";
};

const SummaryMetricCard = ({ label, value, helperText = "", valueClassName = "" }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      <span>{label}</span>
      {helperText ? (
        <span className="ml-1 text-[9px] font-medium normal-case tracking-normal text-slate-400 dark:text-slate-500">
          ({helperText})
        </span>
      ) : null}
    </p>
    <p className={`mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100 ${valueClassName}`}>
      {value}
    </p>
  </div>
);

const ShiftHistoryDetail = () => {
  const navigate = useNavigate();
  const { cashSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedStoreId } = useStore();

  const businessDate = searchParams.get("businessDate") || "";
  const dateFrom = searchParams.get("fromDate") || searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("toDate") || searchParams.get("dateTo") || "";
  const tillIdFilter = searchParams.get("tillId") || "";
  const statusFilter = searchParams.get("statuses") || "";

  const {
    data: sessionData,
    isLoading: sessionLoading,
    isError: sessionIsError,
    error: sessionError,
  } = useGetTillSessionQuery(cashSessionId, {
    skip: !cashSessionId,
  });

  const session = sessionData && typeof sessionData === "object" ? sessionData : null;

  const { data: tillsData } = useGetTillsQuery(
    { storeId: session?.storeId || selectedStoreId, activeOnly: false },
    { skip: !selectedStoreId && !session?.storeId },
  );

  const tills = useMemo(() => (Array.isArray(tillsData) ? tillsData : []), [tillsData]);

  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );

  const {
    data: movementsData,
    isLoading: movementsLoading,
    isError: movementsIsError,
    error: movementsError,
  } = useGetCashMovementsQuery(cashSessionId, {
    skip: !cashSessionId,
  });

  const movements = useMemo(
    () =>
      (Array.isArray(movementsData) ? movementsData : []).filter((movement) =>
        isVisibleCashMovementStatus(movement?.status),
      ),
    [movementsData],
  );

  const movementRows = useMemo(
    () =>
      movements.map((movement) => {
        const signedAmount = getMovementSignedAmount(movement.movementType, movement.amount);
        return {
          id: movement.cashMovementId || movement.id,
          dateTime: formatShortDateTime(movement.createdAt || movement.updatedAt),
          movementType: (
            <Badge tone={getCashingUpMovementTone(movement.movementType)}>
              {getCashingUpMovementTypeLabel(movement.movementType)}
            </Badge>
          ),
          amount: (
            <span
              className={`font-semibold ${
                signedAmount >= 0
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-rose-700 dark:text-rose-300"
              }`}
            >
              {formatSignedCurrency(signedAmount)}
            </span>
          ),
          reasonCode: movement.reasonCode || "-",
        };
      }),
    [movements],
  );

  const varianceValue = normalizeMoney(
    session?.cashVariance || session?.varianceAmount || 0,
  );

  const summary = {
    tillName:
      session?.tillName || tillById.get(session?.tillId)?.tillName || session?.tillId || "-",
    businessDate: session?.businessDate || businessDate || "-",
    openedAt: formatShortDateTime(session?.openedAt),
    closedAt: formatShortDateTime(session?.closedAt),
    openingCash: formatCurrency(Number(session?.openingCash || 0)),
    expectedCash: formatCurrency(Number(session?.expectedCash || 0)),
    actualCash: formatCurrency(Number(session?.actualCash || session?.closingCash || 0)),
    totalSales: formatCurrency(Number(session?.totalSales ?? 0)),
    cardSales: formatCurrency(
      Number(session?.cardSales || getTenderSummaryAmount(session, "CARD")),
    ),
    cardTip: formatCurrency(Number(session?.cardTip ?? 0)),
    cardRefunds: formatCurrency(Number(session?.cardRefunds ?? 0)),
    cashRefunds: formatCurrency(Number(session?.cashRefunds ?? 0)),
    variance: formatSignedCurrency(varianceValue),
  };

  const backPath = buildBackPath({
    dateFrom,
    dateTo,
    businessDate,
    tillIdFilter,
    statusFilter,
  });

  if (sessionLoading) {
    return (
      <div className="space-y-6 px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
        <PageHeader title="" subtitle="Review the selected shift summary." />
        <Alert tone="info">Loading shift summary...</Alert>
      </div>
    );
  }

  if (sessionIsError || !session) {
    return (
      <div className="space-y-6 px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
        <PageHeader
          title=""
          subtitle="Review the selected shift summary."
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate(backPath)}>
              Back to History
            </Button>
          }
        />
        <Alert tone="error">{getErrorMessage(sessionError, "Failed to load shift summary.")}</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
      <PageHeader
        title=""
        subtitle="Review the selected shift summary."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => navigate(backPath)}>
              Back to History
            </Button>
          </div>
        }
      />

      <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-4xl rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-200">
            {session.status === "CLOSED"
              ? "This is a final closed shift. Variance is retained for reporting and audit only."
              : "This shift is still active or in progress. Variance is informational and does not block the session."}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <SummaryMetricCard label="Till Name" value={summary.tillName} />
          <SummaryMetricCard label="Business Date" value={summary.businessDate} />
          <SummaryMetricCard label="Opened At" value={summary.openedAt} />

          <SummaryMetricCard label="Closed At" value={summary.closedAt} />
          <SummaryMetricCard label="Starting Cash" value={summary.openingCash} />
          <SummaryMetricCard
            label="Total Sales"
            value={summary.totalSales}
            helperText="Cash + PDQ"
          />

          <SummaryMetricCard
            label="Cash Left in Drawer Now"
            value={summary.expectedCash}
          />
          <SummaryMetricCard label="PDQ Sales" value={summary.cardSales} />
          {/*
          <SummaryMetricCard label="PDQ Tips" value={summary.cardTip} />
          */}

          <SummaryMetricCard label="PDQ Refunds" value={summary.cardRefunds} />
          <SummaryMetricCard label="Counted Cash" value={summary.actualCash} />
          <SummaryMetricCard label="Cash Refund" value={summary.cashRefunds} />
          <SummaryMetricCard
            label="Variance"
            value={summary.variance}
            valueClassName={
              varianceValue >= 0
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-rose-700 dark:text-rose-300"
            }
          />
        </div>

      </Card>

      <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Cash Movements
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Movement-level breakdown for the selected shift.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 dark:text-slate-400">Net Impact</p>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {formatSignedCurrency(
                movements.reduce(
                  (total, movement) =>
                    total +
                    Number(getMovementSignedAmount(movement.movementType, movement.amount) || 0),
                  0,
                ),
              )}
            </p>
          </div>
        </div>

        {movementsIsError && (
          <Alert tone="error">
            {getErrorMessage(movementsError, "Failed to load shift movements.")}
          </Alert>
        )}

        {movementsLoading && <Alert tone="info">Loading movements...</Alert>}

        {!movementsLoading && (
          <Table
            columns={[
              { key: "dateTime", label: "Date & Time" },
              { key: "movementType", label: "Type" },
              { key: "amount", label: "Amount" },
              { key: "reasonCode", label: "Reason" },
            ]}
            rows={movementRows}
            emptyText="No movements recorded for this shift."
          />
        )}
      </Card>
    </div>
  );
};

export default ShiftHistoryDetail;
